import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { paymentAPI, publicAPI } from '../services/api';
import { calculateGST } from '../utils/gst';
import toast from 'react-hot-toast';

// Lazy-load the Razorpay checkout SDK only when a payment is initiated.
let razorpayLoadPromise = null;
function loadRazorpaySDK() {
  if (window.Razorpay) return Promise.resolve();
  if (razorpayLoadPromise) return razorpayLoadPromise;
  razorpayLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = resolve;
    script.onerror = () => { razorpayLoadPromise = null; reject(new Error('Failed to load Razorpay SDK')); };
    document.body.appendChild(script);
  });
  return razorpayLoadPromise;
}

// Per-user pricing constants
const PRICE_PER_USER = { monthly: 599, yearly: 299 };

const SubscriptionExpiredModal = () => {
  const { logout } = useAuth();
  const { organization, plan, expireReason, daysRemaining, billingCycle } = useOrg();
  const [renewing, setRenewing] = useState(false);
  const [error, setError] = useState('');
  const [cycleId, setCycleId] = useState(billingCycle || 'monthly');
  const [numUsers, setNumUsers] = useState(organization?.paidSeats || 1);

  const getTitle = () => {
    switch (expireReason) {
      case 'suspended':
        return 'Account Suspended';
      case 'cancelled':
        return 'Subscription Cancelled';
      case 'trial_expired':
        return 'Free Trial Expired';
      case 'payment_pending':
        return 'Complete Your Payment';
      case 'subscription_expired':
        return 'Subscription Expired';
      default:
        return 'Subscription Required';
    }
  };

  const getMessage = () => {
    switch (expireReason) {
      case 'suspended':
        return 'Your organization has been suspended. Please contact the TeleValley team to reactivate your subscription.';
      case 'cancelled':
        return 'Your subscription has been cancelled. Please renew to continue using TeleValley.';
      case 'trial_expired':
        return 'Your free trial has expired. Renew your subscription to continue using TeleValley.';
      case 'payment_pending':
        return 'Your account is ready. Complete the payment to activate your subscription — it starts immediately, with no trial period.';
      case 'subscription_expired':
        return 'Your subscription has expired. Renew your subscription to regain access to TeleValley.';
      default:
        return 'Your subscription has ended. Please renew to continue using the platform.';
    }
  };

  // Whether we can show the Renew Now button (not for suspended/cancelled — those need admin intervention)
  const canRenew = expireReason === 'trial_expired' || expireReason === 'subscription_expired' || expireReason === 'payment_pending';
  const isFirstPayment = expireReason === 'payment_pending';
  // Trial / free plans have no price, so paying means buying the Full Access plan
  // (a /renew of a ₹0 trial plan is rejected with "Free plans do not require renewal")
  const currentPlanIsPaid = !!(plan && (plan.isPerUser || plan.price?.monthly > 0 || plan.price?.yearly > 0));

  const perUserPrice = PRICE_PER_USER[cycleId];
  const subtotal = perUserPrice * numUsers * (cycleId === 'yearly' ? 12 : 1);
  const gstConfig = plan?.gstConfig || 'applicable';
  const orgState = organization?.address?.state || null;
  const { gstAmount, cgstAmount, sgstAmount, totalAmount, gstLabel, gstType } = calculateGST(subtotal, gstConfig, orgState);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      window.location.href = '/login';
    }
  };

  const handleRenew = async () => {
    if (!plan) {
      setError('Plan information not available. Please contact support.');
      return;
    }

    setRenewing(true);
    setError('');

    try {
      await loadRazorpaySDK();

      const cycle = cycleId;

      // Renew the current paid plan, or buy Full Access after a trial / free plan
      const { data: order } = currentPlanIsPaid
        ? await paymentAPI.createRenewalOrder({ billingCycle: cycle, numUsers })
        : await paymentAPI.createOrder({ planId: 'full-access', billingCycle: cycle, numUsers });

      // Open Razorpay checkout
      const options = {
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'TeleValley',
        description: `Full Access — ${numUsers} telecaller${numUsers > 1 ? 's' : ''} + 1 admin · ${order.cycleLabel} ${isFirstPayment ? 'subscription' : 'renewal'}`,
        order_id: order.orderId,
        handler: async (response) => {
          try {
            const verifyRes = await paymentAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              planId: order.planId,
              billingCycle: cycle,
              numUsers,
            });

            if (verifyRes.data?.success) {
              toast.success(isFirstPayment ? 'Payment successful — your subscription is active! Refreshing...' : 'Subscription renewed successfully! Refreshing...');
              window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
              setTimeout(() => window.location.reload(), 1500);
            }
          } catch (verifyErr) {
            console.error('Payment verification error:', verifyErr);
            setError(verifyErr.response?.data?.message || 'Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: organization?.name || '',
          email: organization?.email || '',
        },
        theme: { color: '#E7000B' },
        modal: {
          ondismiss: () => {
            toast('Payment cancelled', { icon: 'ℹ️' });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        toast.error(`Payment failed: ${resp.error.description}`);
      });
      rzp.open();
    } catch (err) {
      console.error('Renew order error:', err);
      setError(err.response?.data?.message || 'Failed to initiate renewal. Please try again or contact support.');
    } finally {
      setRenewing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const inr = (n) => `₹${n.toLocaleString('en-IN')}`;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
      padding: '12px',
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '16px',
        padding: '20px',
        maxWidth: '420px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        maxHeight: 'calc(100vh - 24px)',
        overflowY: 'auto',
      }}>
        {/* Warning Icon */}
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          backgroundColor: '#FEE2E2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 10px',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#111827',
          marginBottom: '4px',
        }}>
          {getTitle()}
        </h2>

        {/* Message */}
        <p style={{
          fontSize: '13px',
          color: '#6B7280',
          lineHeight: '18px',
          marginBottom: '10px',
        }}>
          {getMessage()}
        </p>

        {/* Plan Info */}
        {plan && (
          <div style={{
            backgroundColor: '#EEF2FF',
            padding: '6px 12px',
            borderRadius: '8px',
            marginBottom: '10px',
            fontSize: '12px',
            color: '#6366F1',
            fontWeight: '600',
          }}>
            {plan.name}
            {billingCycle && billingCycle !== 'monthly' && (
              <span style={{ fontWeight: '400', color: '#818CF8' }}>
                {' '}({billingCycle === 'yearly' ? 'Yearly' : 'Monthly'})
              </span>
            )}
            {!isFirstPayment && (organization?.subscriptionEndsAt || organization?.trialEndsAt) && (
              <span style={{ fontWeight: '400', color: '#818CF8', display: 'block', fontSize: '11px', marginTop: '2px' }}>
                Expired: {formatDate(organization.subscriptionEndsAt || organization.trialEndsAt)}
              </span>
            )}
          </div>
        )}

        {/* Renewal Options — only for expired/trial_expired */}
        {canRenew && (
          <div style={{
            backgroundColor: '#F9FAFB',
            borderRadius: '10px',
            padding: '10px',
            marginBottom: '10px',
            textAlign: 'left',
          }}>
            {/* Cycle toggle + user count in compact rows */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ display: 'flex', gap: '4px' }}>
                {['monthly', 'yearly'].map((c) => (
                  <button
                    key={c}
                    onClick={() => setCycleId(c)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: cycleId === c ? '2px solid #E7000B' : '1px solid #D1D5DB',
                      backgroundColor: cycleId === c ? '#FEF2F2' : '#fff',
                      color: cycleId === c ? '#E7000B' : '#374151',
                      fontWeight: '600',
                      fontSize: '12px',
                      cursor: 'pointer',
                      lineHeight: '1.4',
                    }}
                  >
                    {c === 'monthly' ? 'Monthly' : 'Yearly'}
                    {c === 'yearly' && <span style={{ fontSize: '9px', color: '#16A34A', marginLeft: '2px' }}>−50%</span>}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={() => setNumUsers(Math.max(1, numUsers - 1))}
                  disabled={numUsers <= 1}
                  style={{
                    width: '26px', height: '26px', borderRadius: '4px', border: '1px solid #D1D5DB',
                    backgroundColor: '#fff', cursor: numUsers <= 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px', fontWeight: 'bold', color: '#374151',
                    opacity: numUsers <= 1 ? 0.4 : 1, lineHeight: '1',
                  }}
                >−</button>
                <span style={{ fontSize: '14px', fontWeight: '700', minWidth: '20px', textAlign: 'center' }}>{numUsers}</span>
                <button
                  onClick={() => setNumUsers(Math.min(100, numUsers + 1))}
                  disabled={numUsers >= 100}
                  style={{
                    width: '26px', height: '26px', borderRadius: '4px', border: '1px solid #D1D5DB',
                    backgroundColor: '#fff', cursor: numUsers >= 100 ? 'not-allowed' : 'pointer',
                    fontSize: '14px', fontWeight: 'bold', color: '#374151',
                    opacity: numUsers >= 100 ? 0.4 : 1, lineHeight: '1',
                  }}
                >+</button>
                <span style={{ fontSize: '11px', color: '#6B7280' }}>telecallers</span>
              </div>
            </div>

            {/* Price summary — compact */}
            <div style={{
              borderTop: '1px solid #E5E7EB',
              paddingTop: '8px',
              fontSize: '12px',
              color: '#374151',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span>{inr(perUserPrice)}/telecaller{numUsers > 1 ? 's' : ''} × {numUsers}{cycleId === 'yearly' ? ' × 12 mo' : ''}</span>
                <span style={{ fontWeight: '600' }}>{inr(subtotal)}</span>
              </div>
              {gstType === 'none' ? null : gstType === 'cgst_sgst' ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6B7280' }}>
                    <span>CGST (9%)</span>
                    <span>{inr(cgstAmount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6B7280' }}>
                    <span>SGST (9%)</span>
                    <span>{inr(sgstAmount)}</span>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6B7280' }}>
                  <span>IGST (18%)</span>
                  <span>{inr(gstAmount)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #E5E7EB', fontWeight: '700', fontSize: '13px' }}>
                <span>Total</span>
                <span style={{ color: '#E7000B' }}>{inr(totalAmount)}/{cycleId === 'yearly' ? 'yr' : 'mo'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Inline error message */}
        {error && (
          <div style={{
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '8px',
            padding: '8px 12px',
            marginBottom: '8px',
            fontSize: '13px',
            color: '#B91C1C',
            textAlign: 'left',
          }}>
            {error}
          </div>
        )}

        {/* Renew Now Button — only for expired/trial_expired */}
        {canRenew && (
          <button
            onClick={handleRenew}
            disabled={renewing}
            style={{
              display: 'block',
              width: '100%',
              padding: renewing ? '10px 0' : '11px 0',
              backgroundColor: renewing ? '#9CA3AF' : '#E7000B',
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '700',
              cursor: renewing ? 'not-allowed' : 'pointer',
              marginBottom: '6px',
              transition: 'background-color 0.2s',
            }}
          >
            {renewing ? 'Opening Payment...' : `🔒 ${isFirstPayment ? 'Pay & Activate' : 'Renew Now'} — ${inr(totalAmount)}/${cycleId === 'yearly' ? 'yr' : 'mo'}`}
          </button>
        )}

        {/* Contact — secondary for suspended/cancelled */}
        {!canRenew && (
          <a
            href={`mailto:support@televalley.com?subject=Subscription%20Reactivation%20Request&body=Hi%2C%20I%20would%20like%20to%20reactivate%20my%20TeleValley%20subscription.%0A%0AOrganization%3A%20${organization?.name || ''}%0APlan%3A%20${plan?.name || ''}`}
            style={{
              display: 'block',
              width: '100%',
              padding: '11px 0',
              backgroundColor: '#E7000B',
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '700',
              textDecoration: 'none',
              cursor: 'pointer',
              marginBottom: '6px',
            }}
          >
            Contact TeleValley Team
          </a>
        )}

        <a
          href="tel:+919028247055"
          style={{
            display: 'block',
            width: '100%',
            padding: '10px 0',
            backgroundColor: '#fff',
            color: '#374151',
            border: '1px solid #D1D5DB',
            borderRadius: '10px',
            fontSize: '13px',
            fontWeight: '600',
            textDecoration: 'none',
            cursor: 'pointer',
            marginBottom: '4px',
          }}
        >
          📞 Call Us: +91 90282 47055
        </a>

        {/* Logout */}
        <button
          onClick={handleLogout}
          style={{
            background: 'none',
            border: 'none',
            color: '#9CA3AF',
            fontSize: '12px',
            cursor: 'pointer',
            padding: '4px 12px',
          }}
        >
          Log Out
        </button>
      </div>
    </div>
  );
};

export default SubscriptionExpiredModal;